import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    InputAdornment,
    IconButton,
    Paper,
    Link as MuiLink
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import APIClient from '../utils/axios.util';

// Validation Schema
const validationSchema = Yup.object().shape({
    companyEmail: Yup.string()
        .email('Please enter a valid email address')
        .required('Email is required'),
    password: Yup.string()
        .required('Password is required')
});

const UserLoginForm = () => {

    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const initialValues = {
        companyEmail: '',
        password: ''
    };

    const handleSubmit = (values, { setSubmitting, setErrors }) => {
        console.log('Login attempt:', values.companyEmail);
        const payload = {
            email: values.companyEmail,
            password: values.password
        };

        APIClient.post('/users/login', payload)
            .then((response) => {
                console.log('✅ Login successful:', response.data);
                window.gtag('event', 'login', { method: 'email' });
                // Save user data to localStorage
                localStorage.setItem('UserDetails', JSON.stringify(response.data));

                // Navigate to dashboard demos page
                navigate('/dashboard/demos');
            })
            .catch((error) => {
                console.error('❌ Login failed:', error);

                if (error.response) {
                    const errorMessage = error.response.data.message || 'Login failed. Please check your credentials.';
                    setErrors({ companyEmail: errorMessage });
                } else if (error.request) {
                    setErrors({ companyEmail: 'No response from server. Please try again.' });
                } else {
                    setErrors({ companyEmail: 'An error occurred. Please try again.' });
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f5f5f5',
                padding: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    padding: 4,
                    maxWidth: 450,
                    width: '100%',
                    borderRadius: 2
                }}
            >
                <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
                    Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    Log in to access your demos
                </Typography>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                        <Form>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Email Field */}
                                <TextField
                                    fullWidth
                                    id="companyEmail"
                                    name="companyEmail"
                                    label="Company Email"
                                    type="email"
                                    value={values.companyEmail}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.companyEmail && Boolean(errors.companyEmail)}
                                    helperText={touched.companyEmail && errors.companyEmail}
                                    disabled={isSubmitting}
                                />

                                {/* Password Field */}
                                <TextField
                                    fullWidth
                                    id="password"
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && errors.password}
                                    disabled={isSubmitting}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                {/* Login Button */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={isSubmitting}
                                    sx={{ mt: 1 }}
                                >
                                    {isSubmitting ? 'Logging in...' : 'Log In'}
                                </Button>

                                {/* Register Link */}
                                <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                                    Don't have an account?{' '}
                                    <MuiLink
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => navigate('/register')}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        Register here
                                    </MuiLink>
                                </Typography>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Paper>
        </Box>
    );
};

export default UserLoginForm;
